import React, { useEffect } from 'react';
import { MenuComponent } from '../../../assets/ts/components';
import { MenuInnerWithSub } from './MenuInnerWithSub';
import { MenuItem } from './MenuItem';
import { MyMenuNode } from './useMyMenu';

// Menü Yönetimi'nden gelen ağacı Metronic üst menüsü olarak çizer.
// Seviye 0: üst başlık · Seviye 1: madde / alt başlık (mega'da kolon) · Seviye 2: madde

type Props = {
  tree: MyMenuNode[];
};

// Başlıkların gerçek bir sayfası yoktur; "aktif" vurgusu yanlışlıkla yanmasın
const containerTo = (node: MyMenuNode) => `/__menu/${node.id}`;

const megaWidthClass = (columnCount: number) => (columnCount >= 4 ? 'w-lg-800px' : 'w-lg-600px');
const megaColClass = (columnCount: number) => {
  if (columnCount >= 4) return 'col-lg-3';
  if (columnCount === 3) return 'col-lg-4';
  if (columnCount === 2) return 'col-lg-6';
  return 'col-lg-12';
};

const Leaf: React.FC<{ node: MyMenuNode; hasBullet?: boolean; withIcon?: boolean }> = ({ node, hasBullet = false, withIcon = true }) => (
  <MenuItem to={node.path || '#'} title={node.title} icon={withIcon ? node.icon || undefined : undefined} hasBullet={hasBullet} openNewTab={node.open_new_tab} />
);

const MegaContent: React.FC<{ node: MyMenuNode }> = ({ node }) => {
  // Alt maddesi olanlar kolon; doğrudan eklenmiş maddeler başlıksız son kolonda toplanır
  const columns = node.children.filter((c) => c.children.length > 0);
  const loose = node.children.filter((c) => c.children.length === 0 && c.path);
  const columnCount = columns.length + (loose.length > 0 ? 1 : 0);
  const colClass = megaColClass(columnCount);

  return (
    <div className="row" data-kt-menu-dismiss="true">
      {columns.map((column) => (
        <div key={column.id} className={`${colClass} border-left-lg-1`}>
          <div className="menu-inline menu-column menu-active-bg">
            <div className="menu-item">
              <div className="menu-content">
                <span className="menu-section fs-7 fw-bold text-muted text-uppercase ls-1 py-2">{column.title}</span>
              </div>
            </div>
            {column.children.map((item) => (
              <Leaf key={item.id} node={item} hasBullet={true} withIcon={false} />
            ))}
          </div>
        </div>
      ))}
      {loose.length > 0 && (
        <div className={`${colClass} border-left-lg-1`}>
          <div className="menu-inline menu-column menu-active-bg">
            {loose.map((item) => (
              <Leaf key={item.id} node={item} hasBullet={true} withIcon={false} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const megaColumnCount = (node: MyMenuNode) => {
  const columns = node.children.filter((c) => c.children.length > 0).length;
  const hasLoose = node.children.some((c) => c.children.length === 0 && c.path);
  return columns + (hasLoose ? 1 : 0);
};

export const DynamicMenu: React.FC<Props> = ({ tree }) => {
  // Menü sonradan (istek dönünce) çizildiği için Metronic menü davranışını yeniden bağla
  useEffect(() => {
    MenuComponent.reinitialization();
  }, [tree]);

  return (
    <>
      {tree.map((root) => {
        if (root.children.length === 0) {
          return <Leaf key={root.id} node={root} />;
        }

        if (root.display === 'mega') {
          return (
            <MenuInnerWithSub
              key={root.id}
              isMega={true}
              megaWidthClass={megaWidthClass(megaColumnCount(root))}
              title={root.title}
              to={containerTo(root)}
              icon={root.icon || undefined}
              menuPlacement="bottom-start"
              menuTrigger="click"
            >
              <MegaContent node={root} />
            </MenuInnerWithSub>
          );
        }

        return (
          <MenuInnerWithSub key={root.id} title={root.title} to={containerTo(root)} icon={root.icon || undefined} menuPlacement="bottom-start" menuTrigger="click">
            {root.children.map((child) =>
              child.children.length > 0 ? (
                <MenuInnerWithSub
                  key={child.id}
                  title={child.title}
                  to={containerTo(child)}
                  icon={child.icon || undefined}
                  hasArrow={true}
                  menuPlacement="right-start"
                  menuTrigger={`{default:'click', lg: 'hover'}`}
                >
                  {child.children.map((item) => (
                    <Leaf key={item.id} node={item} hasBullet={true} withIcon={false} />
                  ))}
                </MenuInnerWithSub>
              ) : (
                <Leaf key={child.id} node={child} hasBullet={!child.icon} />
              ),
            )}
          </MenuInnerWithSub>
        );
      })}
    </>
  );
};
